import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Field, Input, Textarea, SaveBar } from "./adminUi";
import { Plus, Trash2 } from "lucide-react";
import { SOCIAL_PLATFORMS, platformIcon } from "../../components/SocialIcons";

const PLATFORM_OPTIONS = Object.entries(SOCIAL_PLATFORMS).map(([value, cfg]) => ({
  value,
  label: cfg.label,
}));

export default function AdminContact() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setContent((c) => ({ ...c, contact: { ...c.contact, [k]: v } }));

  const links = content?.social_links || [];
  const setLinks = (arr) => setContent((c) => ({ ...c, social_links: arr }));
  const updateLink = (i, k, v) => {
    const next = [...links];
    next[i] = { ...next[i], [k]: v };
    setLinks(next);
  };
  const addLink = () => setLinks([...links, { platform: "twitter", url: "", label: "" }]);
  const removeLink = (i) => setLinks(links.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Contact & social updated");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <div className="text-white/60">Loading…</div>;
  const c = content.contact;

  return (
    <div data-testid="admin-contact">
      <p className="text-xs uppercase tracking-[0.25em] text-fuchsia-400">Contact</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-white md:text-4xl">Contact & Social</h1>
      <p className="mt-3 text-white/60">Shown on the contact page and footer.</p>

      <div className="mt-10 rounded-2xl border border-white/10 bg-[#131B2F] p-8">
        <h2 className="text-lg font-semibold text-white">Contact information</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Field label="Email"><Input type="email" value={c.email} onChange={(e) => update("email", e.target.value)} data-testid="contact-email-input" /></Field>
          <Field label="Phone"><Input value={c.phone} onChange={(e) => update("phone", e.target.value)} data-testid="contact-phone-input" /></Field>
          <Field label="Address"><Input value={c.address} onChange={(e) => update("address", e.target.value)} data-testid="contact-address-input" /></Field>
          <Field label="Hours"><Input value={c.hours} onChange={(e) => update("hours", e.target.value)} data-testid="contact-hours-input" /></Field>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-white/10 bg-[#131B2F] p-8" data-testid="social-editor">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Social media accounts</h2>
            <p className="mt-1 text-sm text-white/50">
              Icons appear in the site footer. Leave URL empty to hide an entry. Choose "Custom" for any other platform.
            </p>
          </div>
          <button
            onClick={addLink}
            data-testid="social-add-btn"
            className="group relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white"
          >
            <span className="absolute inset-0 rounded-full gradient-bg" />
            <span className="relative flex items-center gap-1.5"><Plus size={12} /> Add another</span>
          </button>
        </div>

        <div className="mt-6 space-y-3">
          {links.length === 0 && (
            <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-white/40">
              No social accounts yet. Click "Add another" to create one.
            </div>
          )}
          {links.map((l, i) => {
            const Icon = platformIcon(l.platform);
            return (
              <div key={i} className="grid grid-cols-[auto_180px_1fr_auto] items-center gap-3 rounded-xl border border-white/10 bg-[#0F1528] p-3" data-testid={`social-row-${i}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/80">
                  <Icon width={18} height={18} />
                </div>
                <select
                  value={l.platform}
                  onChange={(e) => updateLink(i, "platform", e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-[#0B1020] px-3 py-2 text-sm text-white"
                  data-testid={`social-platform-${i}`}
                >
                  {PLATFORM_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <input
                  type="url"
                  value={l.url}
                  onChange={(e) => updateLink(i, "url", e.target.value)}
                  placeholder="https://..."
                  className="w-full rounded-lg border border-white/10 bg-[#0B1020] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-500/50 focus:outline-none"
                  data-testid={`social-url-${i}`}
                />
                <button
                  onClick={() => removeLink(i)}
                  data-testid={`social-remove-${i}`}
                  className="rounded-lg border border-white/10 p-2 text-white/60 hover:text-white"
                  aria-label="Remove"
                >
                  <Trash2 size={14} />
                </button>
                {l.platform === "custom" && (
                  <div className="col-span-4">
                    <Field label="Custom platform label (shown as tooltip)">
                      <Input value={l.label || ""} onChange={(e) => updateLink(i, "label", e.target.value)} placeholder="e.g. Behance" />
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <SaveBar onSave={save} saving={saving} />
    </div>
  );
}
